import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Coins, FileText, Plane, Ticket, Users } from 'lucide-react';
import { getUserOverview } from '../../services/userCenterService.js';
import { listFlightOrders } from '../../services/flightService.js';
import {
  ORDER_STATUS,
  airportLabel,
  formatMoney,
  formatTime as formatFlightTime,
} from '../../modules/flight/flightData.js';

const STATUS_LABELS = {
  draft: '草稿',
  queued: '排队中',
  processing: '处理中',
  in_progress: '进行中',
  queued_payment: '待支付',
  waiting_captcha: '待验证码',
  waiting_user_action: '待用户操作',
  pending: '待确认',
  confirmed: '已确认',
  paid: '已付款',
  issued: '已出票',
  completed: '已完成',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

const SERVICE_ICONS = {
  ds160: FileText,
  vn_evisa: FileText,
  thailand_arrival_card: Ticket,
  flight: Plane,
};

const formatTime = (value) => {
  if (!value) return '时间未记录';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '时间未记录';
  return date.toLocaleString('zh-CN', { hour12: false });
};

const UserOverview = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [flightOrders, setFlightOrders] = useState([]);
  const [flightOrdersError, setFlightOrdersError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    setFlightOrdersError('');
    try {
      const [overviewData, flightOrderData] = await Promise.all([
        getUserOverview(),
        listFlightOrders({ limit: 5 }),
      ]);
      setOverview(overviewData);
      setFlightOrders(flightOrderData || []);
    } catch (err) {
      setError(err.message || '获取用户总览失败');
      setFlightOrders([]);
      setFlightOrdersError(err.message || '机票订单加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  if (loading) {
    return (
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-40 bg-gray-100 rounded" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="h-24 bg-gray-100 rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-sm text-amber-800">用户总览加载失败：{error}</div>
            <button
              onClick={loadOverview}
              className="px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-sm text-amber-700 hover:bg-amber-100"
            >
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!overview) return null;

  const activeCount = overview.services.reduce((sum, item) => sum + item.in_progress, 0);
  const completedCount = overview.services.reduce((sum, item) => sum + item.completed, 0);

  const summaryCards = [
    {
      label: '积分余额',
      value: overview.points.balance,
      description: overview.commission.is_distributor
        ? `佣金余额 ¥${(overview.commission.balance / 100).toFixed(2)}`
        : '可用于签证与入境卡服务',
      icon: Coins,
    },
    {
      label: '进行中服务',
      value: activeCount,
      description: '跨签证、入境卡与机票订单',
      icon: Activity,
    },
    {
      label: '已完成服务',
      value: completedCount,
      description: '历史完成记录',
      icon: FileText,
    },
    {
      label: '机票订单',
      value: overview.services.find((item) => item.service === 'flight')?.total || 0,
      description: '国内 / 国际机票',
      icon: Plane,
    },
  ];

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl font-bold text-gray-900">用户总览</h2>
            <p className="text-sm text-gray-500">
              同一账号，统一管理签证、机票、积分与分销服务
            </p>
          </div>
          {overview.commission.is_distributor && (
            <button
              onClick={() => navigate('/commission/info')}
              className="inline-flex items-center gap-2 rounded-lg bg-purple-50 px-3 py-2 text-sm font-medium text-purple-700 border border-purple-200 hover:bg-purple-100"
            >
              <Users className="w-4 h-4" />
              分销状态：{overview.commission.status}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {summaryCards.map(({ label, value, description, icon: Icon }) => (
            <div
              key={label}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
            >
              <div className="flex items-center justify-between text-gray-500">
                <span className="text-sm">{label}</span>
                <Icon className="w-4 h-4" />
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
              <div className="mt-1 text-xs text-gray-500 truncate">{description}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">服务概览</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {overview.services.map((service) => {
                const Icon = SERVICE_ICONS[service.service] || FileText;
                return (
                  <button
                    key={service.service}
                    onClick={() => navigate(service.path)}
                    className="rounded-xl border border-gray-200 bg-white p-4 text-left hover:border-blue-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <Icon className="w-4 h-4 text-blue-600" />
                        {service.title}
                      </span>
                      <span className="text-xs text-gray-400">共 {service.total}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                        进行中 {service.in_progress}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
                        已完成 {service.completed}
                      </span>
                      {service.draft > 0 && (
                        <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                          草稿 {service.draft}
                        </span>
                      )}
                      {service.failed > 0 && (
                        <span className="px-2 py-1 rounded-full bg-red-50 text-red-700">
                          失败 {service.failed}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">最近动态</h3>
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
              {overview.recent_activities.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  暂无服务记录
                </div>
              ) : (
                overview.recent_activities.map((activity) => (
                  <button
                    key={`${activity.service}-${activity.id}`}
                    onClick={() => navigate(activity.path)}
                    className="w-full px-4 py-3 bg-white text-left hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-gray-900">
                          {activity.title}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {formatTime(activity.created_at)}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                        {STATUS_LABELS[activity.status] || activity.status}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">我的机票订单</h3>
              <p className="text-xs text-gray-500">展示最近 5 条订单</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/flight')}
                className="px-3 py-1.5 rounded-lg border border-sky-200 bg-sky-50 text-sm text-sky-700 hover:bg-sky-100"
              >
                去查询机票
              </button>
              <button
                onClick={() => navigate('/flight/orders')}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50"
              >
                查看全部订单
              </button>
            </div>
          </div>

          {flightOrdersError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              机票订单加载失败：{flightOrdersError}
            </div>
          ) : flightOrders.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              暂无机票订单
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
              {flightOrders.map((order) => {
                const segments = order.offer_snapshot?.segments || [];
                const firstSegment = segments[0] || {};
                const status = ORDER_STATUS[order.status] || {
                  label: order.status,
                };
                return (
                  <button
                    key={order.order_id}
                    onClick={() => navigate(`/flight/orders/${order.order_id}`)}
                    className="w-full bg-white px-4 py-4 text-left hover:bg-gray-50"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">
                            {airportLabel(order.search?.origin)} →{' '}
                            {airportLabel(order.search?.destination)}
                          </span>
                          {firstSegment.flight_number && (
                            <span className="text-xs text-gray-500">
                              {firstSegment.flight_number}
                              {firstSegment.airline_name_zh
                                ? ` · ${firstSegment.airline_name_zh}`
                                : ''}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {order.search?.depart_date || '日期待确认'} ·{' '}
                          {order.passengers?.length || 0} 位乘机人 · 订单号{' '}
                          {order.order_id}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-sky-700">
                          {formatMoney(order.total_price_cny, order.currency)}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-sky-50 text-sky-700">
                          {status.label}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      提交于{' '}
                      {order.created_at
                        ? formatFlightTime(order.created_at)
                        : '时间未记录'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserOverview;
